/** @format */

export type LocaleTree = { [key: string]: string | LocaleTree };
export type FlatLocaleMap = Record<string, string>;
export type FlatLocalesByCode = Record<string, FlatLocaleMap>;
export type LocalesByCode = Record<string, LocaleTree>;

export function flattenLocaleObject(obj: LocaleTree, prefix = ''): FlatLocaleMap {
  const result: FlatLocaleMap = {};

  for (const [key, value] of Object.entries(obj)) {
    const dottedKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenLocaleObject(value as LocaleTree, dottedKey));
    } else {
      result[dottedKey] = String(value ?? '');
    }
  }

  return result;
}

const localeModules = import.meta.glob('../locales/*.json', { eager: true }) as Record<
  string,
  { default?: LocaleTree } | LocaleTree
>;

export function loadAllLocales(): { locales: LocalesByCode; flatLocales: FlatLocalesByCode } {
  const locales: LocalesByCode = {};
  const flatLocales: FlatLocalesByCode = {};

  Object.entries(localeModules).forEach(([filePath, module]) => {
    const fileName = filePath.split('/').pop() || '';
    const locale = fileName.replace(/\.json$/, '');
    if (!locale) return;

    const raw = ((module as { default?: LocaleTree }).default ?? module) as LocaleTree;
    locales[locale] = raw;
    flatLocales[locale] = flattenLocaleObject(raw);
  });

  return { locales, flatLocales };
}

export function sortLocaleCodes(localeCodes: string[]): string[] {
  const sorted = [...localeCodes].sort((a, b) => a.localeCompare(b));
  const enIndex = sorted.indexOf('en');
  if (enIndex > 0) {
    sorted.splice(enIndex, 1);
    sorted.unshift('en');
  }
  return sorted;
}

export function collectAllLocaleKeys(flatLocales: FlatLocalesByCode): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  // Prefer English key order (matches en.json), then append keys only present in other locales.
  const localeOrder = sortLocaleCodes(Object.keys(flatLocales));
  localeOrder.forEach(locale => {
    Object.keys(flatLocales[locale] || {}).forEach(key => {
      if (seen.has(key)) return;
      seen.add(key);
      keys.push(key);
    });
  });

  return keys;
}
